import * as z from 'zod';
// ---------------------------------------------------------------------------
// Nav L2 (recursive tree structure)
// ---------------------------------------------------------------------------
const NavL2ItemSchema = z.lazy(() => z.object({
    id: z.string(),
    label: z.string(),
    path: z.string().optional(),
    children: z.array(NavL2ItemSchema).optional()
}));
const NavL2SectionSchema = z.object({
    id: z.string(),
    label: z.string().optional(),
    items: z.array(NavL2ItemSchema)
});
/**
 * Input shape for L2 config: either `{ sections }` or flat `{ items }`.
 * Exported separately for JSON schema generation (transforms can't be serialised).
 */
const NavL2ConfigInputSchema = z.union([
    z.object({
        title: z.string().optional(),
        sections: z.array(NavL2SectionSchema)
    }),
    z.object({
        title: z.string().optional(),
        items: z.array(NavL2ItemSchema)
    })
]);
/**
 * Accepts either `{ sections }` or flat `{ items }` and normalises to sections form.
 */
const NavL2ConfigSchema = NavL2ConfigInputSchema.transform(l2 => {
    if ('sections' in l2) {
        return l2;
    }
    return {
        title: l2.title,
        sections: [
            {
                id: 'default',
                label: '',
                items: l2.items
            }
        ]
    };
});
// ---------------------------------------------------------------------------
// Nav Items
// ---------------------------------------------------------------------------
const NavItemsSchema = z.object({
    id: z.string(),
    label: z.string(),
    icon: z.string(),
    path: z.string(),
    group: z.enum(['primary', 'pinned', 'bottom']).optional(),
    hideL2: z.boolean().optional().default(false),
    l2: NavL2ConfigSchema.optional()
});
// ---------------------------------------------------------------------------
// Menu Items (used by HRD header/footer menus)
// ---------------------------------------------------------------------------
const MenuBaseSchema = NavItemsSchema.extend({
    isHeading: z.boolean().optional().default(false),
    isDivider: z.boolean().optional().default(false),
    icon: z.string().optional(),
    path: z.string().optional()
});
const MenuItemsSchema = MenuBaseSchema.extend({
    children: z.array(MenuBaseSchema).optional()
});
// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------
const AuthSchema = z.object({
    disabled: z.boolean().optional().default(false),
    authApi: z.url(),
    logLevel: z.enum(['Error', 'Warning', 'Info', 'Verbose', 'Trace']).optional().default('Verbose'),
    redirectUri: z.url(),
    postLogoutRedirectUri: z.url(),
    clientId: z.string(),
    suppressRememberMe: z.boolean().optional().default(false),
    homeRealmDiscovery: z.boolean().optional().default(true),
    tokensToLocalStorage: z.boolean().optional().default(true),
    hrdHeaderMenuItems: z.array(MenuItemsSchema).optional(),
    hrdFooterMenuItems: z.array(MenuItemsSchema).optional(),
    scope: z.string().optional(),
    authority: z.url().optional(),
    knownAuthorities: z.array(z.string()).optional()
});
// ---------------------------------------------------------------------------
// Pendo
// ---------------------------------------------------------------------------
const PendoSchema = z.object({
    disabled: z.boolean().optional().default(false),
    publicAppId: z.string(),
    env: z.enum(['io', 'eu', 'jpn', 'us1', 'au', 'gov']).optional().default('io'),
    contextUrl: z.string().optional().default('/pendo-context'),
    apiKey: z.string().optional()
});
// Capabilities (declarative service/event contracts)
// ---------------------------------------------------------------------------
const CapabilitiesSchema = z.object({
    provides: z.array(z.string()).optional(),
    consumes: z.array(z.string()).optional()
});
// ---------------------------------------------------------------------------
// Sidebar (base — no browser transforms)
// ---------------------------------------------------------------------------
const NavSourceSchema = z.enum(['config', 'server']).optional().default('config');
const SidebarSchema = z.object({
    remoteSidebarMount: z.string(),
    navItems: z.array(NavItemsSchema).optional(),
    navSource: NavSourceSchema,
    rootNavLabel: z.string().optional().default('Portal Home'),
    capabilities: CapabilitiesSchema.optional()
});
// ---------------------------------------------------------------------------
// App (base — no browser transforms)
// ---------------------------------------------------------------------------
const AppSchema = z.object({
    path: z.string(),
    basePath: z.string(),
    appName: z.string(),
    collapseSidebarOnLoad: z.boolean().optional().default(false),
    remotePageMount: z.string().optional(),
    remoteNavItems: z.string().optional(),
    remoteUserMenuItems: z.string().optional(),
    remoteHeaderActionsMount: z.string().optional(),
    navItems: z.array(NavItemsSchema).optional(),
    capabilities: CapabilitiesSchema.optional()
});
// ---------------------------------------------------------------------------
// Remotes
// ---------------------------------------------------------------------------
const RemotesSchema = z.record(z.string(), z.string()).optional().default({});
// ---------------------------------------------------------------------------
// Top-level portal-config schema
// ---------------------------------------------------------------------------
const PortalConfigSchema = z.object({
    auth: AuthSchema,
    pendo: PendoSchema.optional(),
    sidebar: SidebarSchema,
    remotes: RemotesSchema,
    apps: z.array(AppSchema)
});
// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------
export { AppSchema, AuthSchema, CapabilitiesSchema, MenuBaseSchema, MenuItemsSchema, NavItemsSchema, NavL2ConfigInputSchema, NavL2ConfigSchema, NavL2ItemSchema, NavL2SectionSchema, NavSourceSchema, PendoSchema, PortalConfigSchema, RemotesSchema, SidebarSchema };
//# sourceMappingURL=index.js.map